// Confirmação da eleição do processador PADRÃO (aba Nova Implementação).
//
// Confirma, e não grava direto, porque a eleição muda qual credencial responde
// por client token/tokenização do par inteiro — e o dialog nomeia quem GANHA e
// quem PERDE o padrão para o operador reconhecer um clique errado a tempo.
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import type { useSetDefaultProcessorConfigMutation } from "@/services/admin"
import type { ProcessorConfigItem } from "@/types/admin-processor-config"

const prefix = "pages.adminProcessorConfig.byMethod"

export interface PendingDefaultElection {
  /** Config que GANHA o padrão. */
  item: ProcessorConfigItem
  /** Config que PERDE o padrão; `null` quando o par está sem padrão. */
  currentDefault: ProcessorConfigItem | null
}

interface SetDefaultDialogProps {
  /** `null` mantém o diálogo fechado. */
  pending: PendingDefaultElection | null
  /** Nome exibível da config (credencial ou gateway) — resolvido pelo painel. */
  displayNameOf: (item: ProcessorConfigItem) => string
  /**
   * Instância ÚNICA da mutation, criada pelo PAINEL: é dela que o
   * `pendingIds` lê o uuid em voo para travar a linha durante a eleição —
   * uma instância própria aqui deixaria o painel cego ao estado.
   */
  setDefaultMutation: ReturnType<
    typeof useSetDefaultProcessorConfigMutation
  >
  onClose: () => void
}

export const SetDefaultDialog = ({
  pending,
  displayNameOf,
  setDefaultMutation,
  onClose,
}: SetDefaultDialogProps) => {
  const { t } = useTranslation()

  const methodLabel = pending
    ? t(
        `pages.adminProcessorConfig.methods.${pending.item.method}`,
        { defaultValue: pending.item.method }
      )
    : ""

  // A mutation é otimista: confirmar fecha na hora, e o retorno chega pelo
  // próprio cache (badge/switch) + toast — mesmo contrato do deactivate.
  const handleConfirm = () => {
    if (pending) {
      setDefaultMutation.mutate({
        uuid: pending.item.id,
        whiteLabelId: pending.item.whiteLabelId,
        method: pending.item.method,
        processorName: displayNameOf(pending.item),
      })
    }
    onClose()
  }

  const descriptionParams = {
    method: methodLabel,
    whiteLabel:
      pending?.item.whiteLabelName ??
      pending?.item.whiteLabelId ??
      "",
    to: pending ? displayNameOf(pending.item) : "",
    from: pending?.currentDefault
      ? displayNameOf(pending.currentDefault)
      : "",
  }

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`${prefix}.setDefault.title`)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pending?.currentDefault
              ? t(
                  `${prefix}.setDefault.description`,
                  descriptionParams
                )
              : t(
                  `${prefix}.setDefault.descriptionNoPrevious`,
                  descriptionParams
                )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t(`${prefix}.setDefault.cancel`)}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            {t(`${prefix}.setDefault.confirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
